import { IconAlertTriangle, IconHelpCircle } from '@tabler/icons-react';
import { Radio, Stack, Text } from '@mantine/core';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import type { SecurityOptionRes, TransactionScope } from './BindPanel.types.ts';
import { securityDisplayName, spansIssuers } from './scopedBinding.utils.ts';
import { securityPickerData } from './securityScopeCard.utils.ts';
import {
	ApplyToAllLink,
	EyebrowLabel,
	ResolveToCardBox,
	ScopeNoticeRow,
	ScopeWarningRow,
	SecurityScopeRow,
	SecuritySelect,
	TransactionScopeRadio,
} from './bindPanel.styled.ts';

interface Props {
	securities: SecurityOptionRes[];
	securityId: string;
	transactionScope: TransactionScope;
	onChangeSecurity: (securityId: string) => void;
	onChangeTransactionScope: (scope: TransactionScope) => void;
	/** Omitted by consumers that don't own the full binding set (the template
	 * editor binds one inline token at a time), which hides the action. */
	onApplyToAll?: (securityId: string) => void;
}

/**
 * The "WHICH SECURITY?" card: pin one programme security, choose whether the
 * blank speaks about its in-progress or its most recently completed
 * transaction, and see up front whether either will actually render for the
 * previewed investor.
 *
 * The warning is the point. A pinned security renders empty for an investor
 * with no position in it — intended behaviour, but invisible at authoring time
 * unless it is said out loud here.
 *
 * When there is no real preview subject the flags are `null` and the card says
 * so rather than saying nothing. Silence in the place a warning would appear
 * reads as "checked, and it renders" — which is what the Word editor showed by
 * default, since it opens on the sample investor.
 */
export const SecurityScopeCard = ({
	securities,
	securityId,
	transactionScope,
	onChangeSecurity,
	onChangeTransactionScope,
	onApplyToAll,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const selected = securities.find((security) => security.id === securityId);
	const selectedName = selected ? securityDisplayName(selected, spansIssuers(securities)) : '';
	// A binding can name a security that has since been deleted: the id is restored from the
	// saved expression without being checked against the programme, and `securities` only ever
	// lists current ones. The card then had a value the picker could not show and no advisory
	// row fired at all, so it read as unpinned while the expression kept filtering on the dead
	// id and rendered blank for every recipient.
	const isMissing = securityId !== '' && selected === undefined;

	// Which flag matters depends on the radio: completed-transaction and holding
	// fields read `data.holdings`, in-progress fields read `data.allocations`.
	// `null` is "unknown" (no real preview subject): it must not warn, and it
	// must not pass for a clean check either — it gets its own row below.
	const willRender =
		transactionScope === 'completed' ? selected?.isHeld : selected?.hasOpenAllocation;

	const handleScope = (value: string) => {
		if (value === 'in-progress' || value === 'completed') {
			onChangeTransactionScope(value);
		}
	};

	return (
		<ResolveToCardBox $variant={selected ? 'selected' : 'empty'}>
			<EyebrowLabel>
				{tMipManage('master_documents.field_binding.security.which_security')}
			</EyebrowLabel>

			<SecurityScopeRow>
				<SecuritySelect
					searchable
					size="sm"
					allowDeselect={false}
					aria-label={tMipManage('master_documents.field_binding.security.select_aria')}
					placeholder={tMipManage(
						'master_documents.field_binding.security.select_placeholder'
					)}
					value={securityId || null}
					onChange={(value) => value && onChangeSecurity(value)}
					data={securityPickerData(
						securities,
						tMipManage('master_documents.field_binding.security.not_held_suffix')
					)}
				/>
			</SecurityScopeRow>

			<TransactionScopeRadio value={transactionScope} onChange={handleScope}>
				{/* Radio.Group lays children out with no gap of its own. */}
				<Stack gap="xs">
					<Radio
						value="in-progress"
						label={tMipManage(
							'master_documents.field_binding.security.scope.in_progress'
						)}
					/>
					<Radio
						value="completed"
						label={tMipManage(
							'master_documents.field_binding.security.scope.completed'
						)}
					/>
				</Stack>
			</TransactionScopeRadio>

			{isMissing && (
				<ScopeWarningRow data-testid="security-scope-missing">
					<IconAlertTriangle size={16} />
					<Text size="xs">
						{tMipManage('master_documents.field_binding.security.warning_missing', {
							defaultValue:
								'The security this field was bound to is no longer in this programme, so it will render blank. Pick one from the list.',
						})}
					</Text>
				</ScopeWarningRow>
			)}

			{selected && willRender === null && (
				<ScopeNoticeRow data-testid="security-scope-unknown">
					<IconHelpCircle size={16} />
					<Text size="xs">
						{tMipManage('master_documents.field_binding.security.scope_unchecked', {
							security: selectedName,
							defaultValue:
								'No preview recipient is chosen, so whether {{security}} will render for a real investor has not been checked. Pick one under "Preview as".',
						})}
					</Text>
				</ScopeNoticeRow>
			)}

			{selected && willRender === false && (
				<ScopeWarningRow data-testid="security-scope-warning">
					<IconAlertTriangle size={16} />
					<Text size="xs">
						{transactionScope === 'completed'
							? tMipManage(
									'master_documents.field_binding.security.warning_not_held',
									{
										security: selectedName,
									}
								)
							: tMipManage(
									'master_documents.field_binding.security.warning_no_allocation',
									{ security: selectedName }
								)}
					</Text>
				</ScopeWarningRow>
			)}

			{/* `selected`, not `securityId`: one click rewrites every other scoped binding in the
			    document, so it must never spread a security the programme no longer has. */}
			{onApplyToAll && selected && (
				<ApplyToAllLink
					type="button"
					data-testid="security-scope-apply-all"
					onClick={() => onApplyToAll(selected.id)}
				>
					{tMipManage('master_documents.field_binding.security.apply_to_all')}
				</ApplyToAllLink>
			)}
		</ResolveToCardBox>
	);
};
